import { useGameSelector, useShallow } from "#/game/GameStoreProvider";
import { hiddenCaptureBarTileKeysFromSelection } from "#/game/selection/captureResetConfirmation";
import {
  attackableTileKeysFromSelection,
  attackableUnitIdsFromSelection,
  attackRangeFlashStyleFromSelection,
  attackRangeFlashTileKeysFromSelection,
  boardActionHintFromState,
  boardCursorFromState,
  canEndTurnFromState,
  canUndoFromState,
  captureBarHoverPreviewFromState,
  hasUnactedOwnUnitsFromState,
  inspectedUnitFromState,
  pathKeysFromSelection,
  pendingAttackFromSelection,
  previewUnitFromSelection,
  reachableKeysFromSelection,
  selectedUnitIdFromSelection,
} from "#/game/store/selectors";
import { useCoarsePointer } from "#/hooks/useCoarsePointer";

export const useSelectedUnitId = () =>
  useGameSelector((s) => selectedUnitIdFromSelection(s.selection));

export const useReachableKeys = () =>
  useGameSelector(useShallow((s) => reachableKeysFromSelection(s.selection, s.localState)));

export const useAttackableTileKeys = () =>
  useGameSelector(useShallow((s) => attackableTileKeysFromSelection(s.selection)));

export const useAttackRangeFlashTileKeys = () =>
  useGameSelector(
    useShallow((s) =>
      attackRangeFlashTileKeysFromSelection({
        selection: s.selection,
        state: s.localState,
      }),
    ),
  );

export const useAttackRangeFlashStyle = () =>
  useGameSelector((s) =>
    attackRangeFlashStyleFromSelection({
      selection: s.selection,
      state: s.localState,
    }),
  );

export const usePathKeys = () =>
  useGameSelector(useShallow((s) => pathKeysFromSelection(s.selection)));

export const useHiddenCaptureBarTileKeys = () =>
  useGameSelector(
    useShallow((s) => hiddenCaptureBarTileKeysFromSelection(s.selection, s.localState)),
  );

export const useCaptureBarHoverPreview = () =>
  useGameSelector(useShallow((s) => captureBarHoverPreviewFromState(s)));

export const useAttackableUnitIds = () =>
  useGameSelector(useShallow((s) => attackableUnitIdsFromSelection(s.selection)));

export const usePreviewUnit = () =>
  useGameSelector(useShallow((s) => previewUnitFromSelection(s.selection, s.localState.units)));

export const usePendingAttack = () =>
  useGameSelector(
    useShallow((s) => pendingAttackFromSelection({ selection: s.selection, state: s.localState })),
  );

export const useInspectedUnit = () => useGameSelector(inspectedUnitFromState);

export const useCanEndTurn = () => useGameSelector(canEndTurnFromState);

export const useHasUnactedOwnUnits = () => useGameSelector(hasUnactedOwnUnitsFromState);

export const useUnmovedHighlightUnitIds = () =>
  useGameSelector(useShallow((s) => s.unmovedHighlight?.unitIds ?? []));

export const useUnmovedHighlightFadeStartedAtMs = () =>
  useGameSelector((s) => s.unmovedHighlight?.fadeStartedAtMs ?? null);

export const useCanUndo = () => useGameSelector(canUndoFromState);

export const useBoardCursor = () => {
  const coarsePointer = useCoarsePointer();
  return useGameSelector((s) => boardCursorFromState(s, coarsePointer));
};

export const useBoardActionHint = () => {
  const coarsePointer = useCoarsePointer();
  return useGameSelector(useShallow((s) => boardActionHintFromState(s, coarsePointer)));
};

export const usePreviewAnimation = () =>
  useGameSelector(
    useShallow((s) => {
      const previewUnit = previewUnitFromSelection(s.selection, s.localState.units);
      return previewUnit && s.previewAnimation && s.previewAnimation.unitId === previewUnit.unitId
        ? s.previewAnimation
        : null;
    }),
  );
